import { useMemo, useState, type CSSProperties } from 'react';
import type { Ticket } from '../../types/tickets';
import { formatTicketDateTime } from '../../lib/ticketComposeHelpers';

type Props = {
  tickets: Ticket[];
  selectedId: string | null;
  bulkSelectedIds: Set<string>;
  clientNameById: Map<string, string>;
  /** Prefer linked CRM contact name under the subject. */
  contactNameById?: Map<string, string>;
  onSelect: (id: string) => void;
  onToggleBulk: (id: string, checked: boolean) => void;
  onToggleAllBulk: (checked: boolean) => void;
  loading?: boolean;
  statusLabels?: Record<string, { name: string; color?: string }>;
};

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Low',
  normal: '',
  high: 'High',
  urgent: 'Urgent',
};

const UNASSIGNED_GROUP = '__unassigned__';

function statusBadgeStyle(color?: string): CSSProperties | undefined {
  if (!color) return undefined;
  return {
    background: `${color}18`,
    color,
    borderColor: `${color}40`,
  };
}

function sortTicketsNewestFirst(items: Ticket[]): Ticket[] {
  return [...items].sort(
    (a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime(),
  );
}

function groupTickets(tickets: Ticket[], clientNameById: Map<string, string>) {
  const unassigned: Ticket[] = [];
  const byCompany = new Map<string, Ticket[]>();

  for (const ticket of tickets) {
    if (!ticket.client_id) {
      unassigned.push(ticket);
    } else {
      const company = clientNameById.get(String(ticket.client_id)) || 'Unknown account';
      const bucket = byCompany.get(company) ?? [];
      bucket.push(ticket);
      byCompany.set(company, bucket);
    }
  }

  const companyGroups = [...byCompany.entries()]
    .map(([name, items]) => ({
      key: name,
      label: name,
      tickets: sortTicketsNewestFirst(items),
      latestAt: items[0]?.last_message_at ?? '',
    }))
    .sort((a, b) => {
      const aTime = a.tickets[0] ? new Date(a.tickets[0].last_message_at).getTime() : 0;
      const bTime = b.tickets[0] ? new Date(b.tickets[0].last_message_at).getTime() : 0;
      return bTime - aTime;
    });

  for (const group of companyGroups) {
    group.latestAt = group.tickets[0]?.last_message_at ?? '';
  }

  return {
    unassigned: sortTicketsNewestFirst(unassigned),
    companyGroups,
  };
}

function TicketRow({
  ticket,
  selectedId,
  bulkSelectedIds,
  statusLabels,
  clientNameById,
  contactNameById,
  onSelect,
  onToggleBulk,
}: {
  ticket: Ticket;
  selectedId: string | null;
  bulkSelectedIds: Set<string>;
  statusLabels?: Record<string, { name: string; color?: string }>;
  clientNameById: Map<string, string>;
  contactNameById?: Map<string, string>;
  onSelect: (id: string) => void;
  onToggleBulk: (id: string, checked: boolean) => void;
}) {
  const active = ticket.id === selectedId;
  const bulkChecked = bulkSelectedIds.has(ticket.id);
  const statusMeta = statusLabels?.[ticket.status];
  const statusLabel = statusMeta?.name ?? ticket.status.replace(/_/g, ' ');
  const priorityLabel = PRIORITY_LABEL[ticket.priority] ?? '';
  const contactName = ticket.contact_id
    ? contactNameById?.get(String(ticket.contact_id))
    : undefined;
  const accountName = ticket.client_id ? clientNameById.get(String(ticket.client_id)) : undefined;
  const subtitle =
    contactName || accountName || ticket.requester_name || ticket.requester_email || 'Unknown';

  return (
    <li>
      <div
        className={`tickets-list__row${active ? ' tickets-list__row--active' : ''}${
          ticket.is_unread ? ' tickets-list__row--unread' : ''
        }${bulkChecked ? ' tickets-list__row--bulk-selected' : ''}`}
      >
        <label className="tickets-list__check" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={bulkChecked}
            onChange={(e) => onToggleBulk(ticket.id, e.target.checked)}
            aria-label={`Select ${ticket.ticket_number}`}
          />
        </label>
        <button
          type="button"
          draggable
          className="tickets-list__main"
          onClick={() => onSelect(ticket.id)}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/ticket-id', ticket.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
        >
          <div className="tickets-list__top">
            <span className="tickets-list__number">
              {ticket.is_unread ? <span className="tickets-list__unread-dot" aria-hidden /> : null}
              {ticket.ticket_number}
            </span>
            <span className="tickets-list__time">{formatTicketDateTime(ticket.last_message_at)}</span>
          </div>
          <p className="tickets-list__subject">{ticket.subject || '(no subject)'}</p>
          <div className="tickets-list__meta">
            <span className="tickets-list__from">{subtitle}</span>
            <div className="tickets-list__badges">
              {priorityLabel && ticket.priority !== 'normal' ? (
                <span className={`tickets-list__priority tickets-list__priority--${ticket.priority}`}>
                  {priorityLabel}
                </span>
              ) : null}
              <span
                className={`tickets-list__status tickets-list__status--${ticket.status}`}
                style={statusBadgeStyle(statusMeta?.color)}
              >
                {statusLabel}
              </span>
            </div>
          </div>
        </button>
      </div>
    </li>
  );
}

export function TicketList({
  tickets,
  selectedId,
  bulkSelectedIds,
  clientNameById,
  contactNameById,
  onSelect,
  onToggleBulk,
  onToggleAllBulk,
  loading,
  statusLabels,
}: Props) {
  const allSelected = tickets.length > 0 && tickets.every((t) => bulkSelectedIds.has(t.id));
  const someSelected = tickets.some((t) => bulkSelectedIds.has(t.id));
  const { unassigned, companyGroups } = useMemo(
    () => groupTickets(tickets, clientNameById),
    [tickets, clientNameById],
  );

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

  function toggleGroup(key: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (loading) {
    return <p className="tickets-list__empty">Loading tickets…</p>;
  }
  if (tickets.length === 0) {
    return (
      <div className="tickets-list__empty-wrap">
        <p className="tickets-list__empty">No tickets in this folder.</p>
        <p className="tickets-list__empty-hint">
          New mail appears automatically. Send a test email to the mailbox to verify connectivity.
        </p>
      </div>
    );
  }

  function renderGroup(key: string, label: string, groupTickets: Ticket[]) {
    const collapsed = collapsedGroups.has(key);
    return (
      <section key={key} className="tickets-list-group">
        <button
          type="button"
          className="tickets-list-group__head"
          onClick={() => toggleGroup(key)}
          aria-expanded={!collapsed}
        >
          <span className="tickets-list-group__chevron" aria-hidden>
            {collapsed ? '▸' : '▾'}
          </span>
          <span className="tickets-list-group__label">{label}</span>
          <span className="tickets-list-group__count">{groupTickets.length}</span>
        </button>
        {!collapsed ? (
          <ul className="tickets-list tickets-list--grouped">
            {groupTickets.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                selectedId={selectedId}
                bulkSelectedIds={bulkSelectedIds}
                statusLabels={statusLabels}
                clientNameById={clientNameById}
                contactNameById={contactNameById}
                onSelect={onSelect}
                onToggleBulk={onToggleBulk}
              />
            ))}
          </ul>
        ) : null}
      </section>
    );
  }

  return (
    <div className="tickets-list-wrap">
      <div className="tickets-list__head">
        <label className="tickets-list__check-all">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={(e) => onToggleAllBulk(e.target.checked)}
            aria-label="Select all tickets on this page"
          />
        </label>
        <span className="tickets-list__head-label">Select</span>
      </div>

      {unassigned.length > 0
        ? renderGroup(UNASSIGNED_GROUP, 'No account linked', unassigned)
        : null}

      {companyGroups.map((group) => renderGroup(group.key, group.label, group.tickets))}
    </div>
  );
}
